import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../services/api';
import { useCart } from '../context/CartContext';

export default function Checkout() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { cart, fetchCart } = useCart();
  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState('');
  const [showNewAddress, setShowNewAddress] = useState(false);
  const [newAddr, setNewAddr] = useState({ full_name: '', phone: '', address_line1: '', city: '', state: '', pincode: '' });
  const [placing, setPlacing] = useState(false);

  const couponCode = searchParams.get('coupon') || '';
  const items = cart.items || [];
  const subtotal = Number(cart.total || 0);
  const shipping = subtotal >= 999 ? 0 : 99;

  useEffect(() => {
    api.get('auth/addresses/').then(res => {
      const data = res.data.results || res.data;
      setAddresses(data);
      if (data.length) setSelectedAddress(String(data[0].id));
    }).catch(() => {});
  }, []);

  const addAddress = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post('auth/addresses/', newAddr);
      setAddresses(prev => [...prev, res.data]);
      setSelectedAddress(String(res.data.id));
      setShowNewAddress(false);
      setNewAddr({ full_name: '', phone: '', address_line1: '', city: '', state: '', pincode: '' });
      toast.success('Address added');
    } catch { toast.error('Failed to add address'); }
  };

  const placeOrder = async () => {
    if (!selectedAddress) { toast.error('Select an address'); return; }
    setPlacing(true);
    try {
      const res = await api.post('orders/', {
        address_id: parseInt(selectedAddress),
        payment_method: 'cod',
        coupon_code: couponCode,
        notes: ''
      });
      fetchCart();
      toast.success('Order placed successfully!');
      navigate('/orders');
    } catch { toast.error('Failed to place order'); }
    setPlacing(false);
  };

  const input = { width: '100%', padding: '8px', border: '1px solid #ddd', marginBottom: 8 };

  return (
    <div className="container" style={{ maxWidth: 800, margin: '40px auto' }}>
      <h2 className="section-title" style={{ textAlign: 'left', margin: '0 0 32px' }}>Checkout</h2>

      <section style={{ marginBottom: 32 }}>
        <h3 style={{ fontFamily: 'var(--font-heading)', marginBottom: 12 }}>Delivery Address</h3>
        {addresses.map(a => (
          <div key={a.id} className={`address-card ${selectedAddress === String(a.id) ? 'selected' : ''}`} onClick={() => setSelectedAddress(String(a.id))}>
            <strong>{a.full_name}</strong> · {a.phone}<br />
            {a.address_line1}, {a.address_line2 && `${a.address_line2}, `}{a.city}, {a.state} - {a.pincode}
          </div>
        ))}
        {showNewAddress ? (
          <form onSubmit={addAddress} style={{ padding: 16, border: '1px solid #eee' }}>
            <input placeholder="Full Name" value={newAddr.full_name} onChange={e => setNewAddr({ ...newAddr, full_name: e.target.value })} style={input} required />
            <input placeholder="Phone" value={newAddr.phone} onChange={e => setNewAddr({ ...newAddr, phone: e.target.value })} style={input} required />
            <input placeholder="Address" value={newAddr.address_line1} onChange={e => setNewAddr({ ...newAddr, address_line1: e.target.value })} style={input} required />
            <input placeholder="City" value={newAddr.city} onChange={e => setNewAddr({ ...newAddr, city: e.target.value })} style={input} required />
            <input placeholder="State" value={newAddr.state} onChange={e => setNewAddr({ ...newAddr, state: e.target.value })} style={input} required />
            <input placeholder="Pincode" value={newAddr.pincode} onChange={e => setNewAddr({ ...newAddr, pincode: e.target.value })} style={input} required />
            <button type="submit" className="btn btn-sm btn-primary">Save</button>
          </form>
        ) : (
          <button onClick={() => setShowNewAddress(true)} className="btn btn-sm btn-outline" style={{ marginTop: 8 }}>+ Add New Address</button>
        )}
      </section>

      <section style={{ marginBottom: 32, padding: 20, background: 'var(--light)' }}>
        <h3 style={{ fontFamily: 'var(--font-heading)', marginBottom: 12 }}>Order Summary</h3>
        {items.map(i => (
          <div key={i.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: 14 }}>
            <span>{i.product_name} ({i.size}/{i.color}) × {i.quantity}</span><span>₹{(i.price * i.quantity).toFixed(2)}</span>
          </div>
        ))}
        <div style={{ borderTop: '1px solid #ddd', marginTop: 8, paddingTop: 8, display: 'flex', justifyContent: 'space-between' }}>
          <span>Shipping</span><span>{shipping === 0 ? 'Free' : `₹${shipping}`}</span>
        </div>
        {couponCode && <div style={{ display: 'flex', justifyContent: 'space-between', color: 'green' }}><span>Coupon: {couponCode}</span><span>Applied</span></div>}
      </section>

      <section style={{ marginBottom: 32 }}>
        <h3 style={{ fontFamily: 'var(--font-heading)', marginBottom: 12 }}>Payment Method</h3>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input type="radio" checked readOnly /> Cash on Delivery (COD)
        </label>
        <p style={{ fontSize: 12, color: 'var(--gray)', marginTop: 4 }}>Online payment coming soon</p>
      </section>

      <button onClick={placeOrder} disabled={placing} className="btn btn-primary" style={{ width: '100%', opacity: placing ? 0.6 : 1 }}>
        {placing ? 'Placing Order...' : 'Place Order'}
      </button>
    </div>
  );
}
